import axios from 'axios';

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Injeta o token em toda requisição
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function limparSessao() {
  ['token', 'user', 'fazenda', 'permissoes', 'cargo', 'refresh_token', 'pre_token', 'fazendas_disponiveis', 'pre_user'].forEach(
    (k) => localStorage.removeItem(k)
  );
  window.location.href = '/';
}

// Garante que só existe uma renovação de token em andamento por vez;
// requisições concorrentes que caiam em 401 reaproveitam a mesma promise.
let refreshEmAndamento = null;

function renovarToken() {
  if (!refreshEmAndamento) {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      const semRefreshToken = new Error('Sem refresh token disponível.');
      semRefreshToken.semRefreshToken = true; // sessão nunca teve refresh — não tem como recuperar, desloga mesmo
      refreshEmAndamento = Promise.reject(semRefreshToken);
    } else {
      refreshEmAndamento = axios
        .post(`${process.env.REACT_APP_API_URL}/api/auth/refresh/`, { refresh: refreshToken })
        .then((res) => {
          localStorage.setItem('token', res.data.access);
          // Com ROTATE_REFRESH_TOKENS, cada renovação troca também o
          // refresh token — o antigo é invalidado no servidor (blacklist).
          if (res.data.refresh) {
            localStorage.setItem('refresh_token', res.data.refresh);
          }
          return res.data.access;
        })
        .finally(() => {
          refreshEmAndamento = null;
        });
    }
  }
  return refreshEmAndamento;
}

// Em caso de 401, tenta renovar o access token via refresh token antes de
// deslogar — evita derrubar a sessão a cada 60min ou por uma queda breve de rede.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    if (response?.status === 401 && !config._tentouRenovar) {
      config._tentouRenovar = true;
      try {
        const novoAccess = await renovarToken();
        config.headers.Authorization = `Bearer ${novoAccess}`;
        return api(config);
      } catch (erroRefresh) {
        // Só desloga quando dá pra confirmar que a sessão é mesmo inválida:
        // o servidor recusou o refresh (erroRefresh.response existe) ou
        // nunca houve refresh token pra tentar. Uma falha de REDE tentando
        // renovar (sinal caiu no exato momento em que o access token venceu
        // — comum em campo) não é prova de sessão inválida, e apagar a
        // sessão nesse caso derruba quem só está sem internet.
        if (erroRefresh.response || erroRefresh.semRefreshToken) {
          limparSessao();
        }
        return Promise.reject(error);
      }
    }
    if (response?.status === 401) {
      limparSessao();
    }
    return Promise.reject(error);
  }
);

// A API pagina listagens em páginas de tamanho fixo (DRF PageNumberPagination).
// Repositórios/telas que esperam a lista completa devem usar isto em vez de
// `api.get` direto, senão só os itens da 1ª página chegam à tela.
export async function listarTudo(url, config = {}) {
  const separador = url.includes('?') ? '&' : '?';
  let pagina = 1;
  let resultados = [];
  let primeiraResposta = null;
  for (;;) {
    const resposta = await api.get(`${url}${separador}page=${pagina}`, config);
    primeiraResposta = primeiraResposta || resposta;
    const dados = resposta.data;
    if (!dados || Array.isArray(dados)) {
      return resposta;
    }
    resultados = resultados.concat(dados.results || []);
    if (!dados.next) break;
    pagina += 1;
  }
  return { ...primeiraResposta, data: resultados };
}

// Instância separada para o passo de selecionar fazenda (usa pre_token)
export const apiPreAuth = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

apiPreAuth.interceptors.request.use((config) => {
  const preToken = localStorage.getItem('pre_token');
  if (preToken) {
    config.headers.Authorization = `Bearer ${preToken}`;
  }
  return config;
});
